import { supabase } from './supabase';

export interface ThumbnailOptions {
  width?: number;
  height?: number;
  quality?: number;
  format?: 'webp' | 'jpeg' | 'png';
}

export class ThumbnailService {
  /**
   * Generate a thumbnail from a full-size image URL
   */
  static async generateThumbnail(
    imageUrl: string, 
    options: ThumbnailOptions = {}
  ): Promise<string> {
    try {
      const {
        width = 600,
        height = 600,
        quality = 100,
        format = 'webp'
      } = options;


      // Use Supabase edge function for thumbnail generation
      const { data, error } = await supabase.functions.invoke('generate-thumbnail', {
        body: {
          imageUrl,
          width,
          height,
          quality,
          format
        }
      });

      if (error) {
        console.error('❌ Thumbnail generation error:', error);
        throw new Error(error.message || 'Failed to generate thumbnail');
      }

      if (!data?.thumbnailUrl) {
        throw new Error('No thumbnail URL returned');
      }

      return data.thumbnailUrl;

    } catch (error) {
      console.error('❌ Error in generateThumbnail:', error);
      // Fallback to original image if thumbnail generation fails
      console.warn('🔄 Falling back to original image');
      return imageUrl;
    }
  }

  /**
   * Generate thumbnail from uploaded file before storing
   */
  static async generateThumbnailFromFile(
    file: File,
    options: ThumbnailOptions = {}
  ): Promise<{ thumbnailBlob: Blob; thumbnailUrl: string }> {
    try {
      const {
        width = 600,
        height = 600,
        quality = 100,
        format = 'webp'
      } = options;


      // Create canvas for client-side thumbnail generation
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        throw new Error('Canvas context not available');
      }

      // Load image
      const img = new Image();
      const imageUrl = URL.createObjectURL(file);
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = imageUrl;
      });

      // Calculate dimensions maintaining aspect ratio
      const aspectRatio = img.width / img.height;
      let newWidth = width;
      let newHeight = height;

      if (aspectRatio > 1) {
        // Landscape
        newHeight = width / aspectRatio;
      } else {
        // Portrait or square
        newWidth = height * aspectRatio;
      }

      // Set canvas size
      canvas.width = newWidth;
      canvas.height = newHeight;

      // Enable high-quality rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Draw resized image with high quality
      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      // Convert to blob
      const thumbnailBlob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create thumbnail blob'));
            }
          },
          format === 'webp' ? 'image/webp' : `image/${format}`,
          quality / 100
        );
      });

      // Create URL for preview
      const thumbnailUrl = URL.createObjectURL(thumbnailBlob);

      // Clean up
      URL.revokeObjectURL(imageUrl);

      return { thumbnailBlob, thumbnailUrl };

    } catch (error) {
      console.error('❌ Error in generateThumbnailFromFile:', error);
      throw error;
    }
  }

  /**
   * Upload thumbnail to storage
   */
  static async uploadThumbnail(
    thumbnailBlob: Blob,
    fileName: string,
    folder: string = 'thumbnails'
  ): Promise<string> {
    try {
      // Get current user for proper file path structure
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Create proper path structure: user_id/folder/filename
      const thumbnailFileName = `${user.id}/${folder}/${fileName}_thumb_${Date.now()}.webp`;


      const { data, error } = await supabase.storage
        .from('thumbnails')
        .upload(thumbnailFileName, thumbnailBlob, {
          contentType: 'image/webp',
          cacheControl: '3600'
        });

      if (error) {
        console.error('❌ Thumbnail upload error:', error);
        throw error;
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('thumbnails')
        .getPublicUrl(data.path);

      return urlData.publicUrl;

    } catch (error) {
      console.error('❌ Error in uploadThumbnail:', error);
      throw error;
    }
  }

  /**
   * Generate and upload thumbnail from URL (for existing images)
   */
  static async createAndUploadThumbnailFromUrl(
    imageUrl: string,
    fileName: string,
    options: ThumbnailOptions = {}
  ): Promise<string> {
    try {

      // Fetch the image
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch image');
      }

      const blob = await response.blob();
      const file = new File([blob], fileName, { type: blob.type });

      // Generate thumbnail
      const { thumbnailBlob } = await this.generateThumbnailFromFile(file, options);

      // Upload thumbnail (folder name will be added in uploadThumbnail method)
      const thumbnailUrl = await this.uploadThumbnail(thumbnailBlob, fileName, 'ad-generations');

      return thumbnailUrl;

    } catch (error) {
      console.error('❌ Error in createAndUploadThumbnailFromUrl:', error);
      // Return original URL as fallback
      return imageUrl;
    }
  }
}

export default ThumbnailService;